import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Bot, ChevronRight, ChevronLeft, Send, Settings, X } from 'lucide-react';
import { useStore } from '@/store/useStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';
import { chatApi, handleApiError } from '@/services/api';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'sources' | 'performance';
  content: string;
  timestamp: Date;
  sources?: Source[];
  performance?: PerformanceMetrics;
}

interface Source {
  filename: string;
  document_id: number;
  relevance_score: number;
  summary?: string;
}

interface PerformanceMetrics {
  total_time_seconds: number;
  documents_loaded: number;
  documents_filtered_to: number;
  documents_used: number;
  context_chars: number;
  stage_times: {
    [key: string]: number;
  };
}

interface DocIntelAIChatProps {
  projectId?: number;
  framework?: string;
  documentCount?: number;
  onAsk?: (question: string) => Promise<void>;
}

export function DocIntelAIChat({
  projectId,
  framework,
  documentCount,
  onAsk
}: DocIntelAIChatProps) {
  const { t } = useTranslation();
  const { isChatOpen, toggleChat } = useStore();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  
  // Conversation history key (per project)
  const historyKey = `docintel-chat-history-${projectId || 'global'}`;
  
  // Settings
  const [mode, setMode] = useState<'summaries' | 'full_docs'>('summaries');
  const [model, setModel] = useState('claude');
  const [useStreaming, setUseStreaming] = useState(true);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const hasContext = projectId !== undefined;
  
  // Load conversation history on mount
  useEffect(() => {
    if (projectId) {
      try {
        const saved = localStorage.getItem(historyKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          // Convert timestamp strings back to Date objects
          const restored = parsed.map((msg: any) => ({
            ...msg,
            timestamp: new Date(msg.timestamp)
          }));
          setMessages(restored);
        }
      } catch (error) {
        console.error('Failed to load chat history:', error);
      }
    }
  }, [projectId, historyKey]);
  
  // Save conversation history when messages change
  useEffect(() => {
    if (projectId && messages.length > 0) {
      try {
        localStorage.setItem(historyKey, JSON.stringify(messages));
      } catch (error) {
        console.error('Failed to save chat history:', error);
      }
    }
  }, [messages, projectId, historyKey]);
  
  // Auto-scroll to bottom on new messages and during streaming
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  const handleSendStreaming = async (question: string) => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      toast.error(t('chat.notAuthenticated'));
      return;
    }

    // Create streaming message placeholder
    const streamingMessageId = (Date.now() + 2).toString();
    const streamingMessage: ChatMessage = {
      id: streamingMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, streamingMessage]);

    try {
      const response = await fetch('/api/v1/chat/v2/ask/stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          project_id: projectId,
          question,
          mode,
          model,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('No response body');
      }

      let buffer = '';
      let sources: Source[] = [];
      let performance: PerformanceMetrics | undefined;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.trim() || line.startsWith(':')) continue;

          if (line.startsWith('event:')) {
            // Skip event type line, we parse based on data content
            continue;
          }

          if (line.startsWith('data:')) {
            const data = line.substring(5).trim();
            if (!data) continue;

            try {
              const parsed = JSON.parse(data);

              // Handle different event types
              if (parsed.sources) {
                sources = parsed.sources;
                // Add sources message
                const sourcesMessage: ChatMessage = {
                  id: (Date.now() + 1).toString(),
                  role: 'sources',
                  content: `Found ${sources.length} relevant documents`,
                  timestamp: new Date(),
                  sources,
                };
                setMessages(prev => {
                  // Insert before streaming message
                  const idx = prev.findIndex(m => m.id === streamingMessageId);
                  if (idx > -1) {
                    return [...prev.slice(0, idx), sourcesMessage, ...prev.slice(idx)];
                  }
                  return prev;
                });
              } else if (parsed.token) {
                // Append token to streaming message
                setMessages(prev =>
                  prev.map(m =>
                    m.id === streamingMessageId
                      ? { ...m, content: m.content + parsed.token }
                      : m
                  )
                );
              } else if (parsed.total_time_seconds !== undefined) {
                performance = parsed;
              } else if (parsed.status === 'complete') {
                // Done
                if (performance) {
                  const perfMessage: ChatMessage = {
                    id: (Date.now() + 3).toString(),
                    role: 'performance',
                    content: 'Performance metrics',
                    timestamp: new Date(),
                    performance,
                  };
                  setMessages(prev => [...prev, perfMessage]);
                }
              } else if (parsed.error) {
                throw new Error(parsed.error);
              }
            } catch (e) {
              console.warn('Failed to parse SSE data:', data);
            }
          }
        }
      }
    } catch (error) {
      console.error('Streaming error:', error);
      toast.error(handleApiError(error));

      // Update streaming message with error
      setMessages(prev =>
        prev.map(m =>
          m.id === streamingMessageId
            ? { ...m, content: t('chat.errorProcessing') }
            : m
        )
      );
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const clearHistory = () => {
    if (confirm(t('chat.clearConfirm'))) {
      setMessages([]);
      localStorage.removeItem(historyKey);
      toast.success(t('chat.historyCleared'));
    }
  };
  
  const handleSend = async () => {
    if (!input.trim() || loading || !hasContext) return;
    
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date(),
    };
    
    setMessages(prev => [...prev, userMessage]);
    const question = input;
    setInput('');
    setLoading(true);

    // Use streaming if enabled
    if (useStreaming) {
      await handleSendStreaming(question);
      if (onAsk) {
        await onAsk(question);
      }
      return;
    }
    
    try {
      // Call optimized v2 endpoint (non-streaming)
      const data = await chatApi.askV2({
        project_id: projectId!,
        question,
        mode,
        model,
      });
      
      // Add sources message if available
      if (data.sources && data.sources.length > 0) {
        const sourcesMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          role: 'sources',
          content: `Found ${data.sources.length} relevant documents`,
          timestamp: new Date(),
          sources: data.sources,
        };
        setMessages(prev => [...prev, sourcesMessage]);
      }
      
      // Add answer
      const answerMessage: ChatMessage = {
        id: (Date.now() + 2).toString(),
        role: 'assistant',
        content: data.answer,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, answerMessage]);
      
      // Add performance metrics if available
      if (data.performance) {
        const perfMessage: ChatMessage = {
          id: (Date.now() + 3).toString(),
          role: 'performance',
          content: 'Performance metrics',
          timestamp: new Date(),
          performance: data.performance,
        };
        setMessages(prev => [...prev, perfMessage]);
      }
      
      if (onAsk) {
        await onAsk(question);
      }
      
    } catch (error) {
      console.error('Chat error:', error);
      toast.error(handleApiError(error));
      
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: t('chat.errorProcessing'),
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };
  
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  
  return (
    <div className="relative flex-shrink-0 h-full">
      {/* Edge tab toggle — mirrors the left sidebar tab */}
      <button
        className={cn(
          'fixed top-1/2 -translate-y-1/2 z-40 flex items-center justify-center',
          'w-5 h-12 bg-primary text-primary-foreground rounded-l-lg shadow-md',
          'hover:bg-primary/90 transition-all duration-300',
          isChatOpen ? 'right-96' : 'right-0'
        )}
        onClick={toggleChat}
        aria-label={isChatOpen ? t('chat.collapseChat') : t('chat.expandChat')}
      >
        {isChatOpen ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
      </button>

      {/* Chat panel */}
      <aside
        className={cn(
          'h-full bg-card border-l border-border transition-all duration-300 flex-shrink-0',
          isChatOpen ? 'w-96' : 'w-0',
          'flex flex-col overflow-hidden'
        )}
      >
        {/* Always render content for full height - just translate entire aside */}
        <>
          {/* Header */}
          <div className="p-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Bot className="h-6 w-6 text-primary" />
                <h2 className="font-bold text-lg">{t('chat.docIntelAi')}</h2>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSettings(!showSettings)}
                  className="h-8 w-8 p-0"
                >
                  <Settings className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearHistory}
                  disabled={messages.length === 0}
                  className="h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            {/* Context Badge */}
            {hasContext ? (
              <div className="p-2 bg-white dark:bg-gray-900 rounded border border-blue-200 dark:border-blue-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant="default" className="text-xs">
                      {framework ? framework.toUpperCase() : 'Project'}
                    </Badge>
                    {documentCount !== undefined && (
                      <span className="text-xs text-muted-foreground">
                        • {documentCount} docs
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-2 bg-yellow-50 dark:bg-yellow-950 rounded border border-yellow-200 dark:border-yellow-800">
                <p className="text-xs text-yellow-800 dark:text-yellow-200">
                  ⚠️ {t('chat.openProjectFirst')}
                </p>
              </div>
            )}
            
            {/* Settings Panel */}
            {showSettings && (
              <div className="mt-3 p-3 bg-white dark:bg-gray-900 rounded border space-y-3">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold">{t('chat.model')}</Label>
                  <Select value={model} onValueChange={setModel}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="claude">{t('chat.modelClaude')}</SelectItem>
                      <SelectItem value="openai">{t('chat.modelOpenai')}</SelectItem>
                      <SelectItem value="ollama-14b">{t('chat.modelOllama')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="full-docs"
                    checked={mode === 'full_docs'}
                    onCheckedChange={(checked) => setMode(checked ? 'full_docs' : 'summaries')}
                  />
                  <Label htmlFor="full-docs" className="text-xs cursor-pointer">
                    {t('chat.queryFullDocs')}
                  </Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="streaming"
                    checked={useStreaming}
                    onCheckedChange={(checked) => setUseStreaming(!!checked)}
                  />
                  <Label htmlFor="streaming" className="text-xs cursor-pointer">
                    {t('chat.streamResponses')}
                  </Label>
                </div>
                
                <div className="pt-3 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearHistory}
                    className="w-full text-xs"
                  >
                    {t('chat.clearChat')}
                  </Button>
                  {messages.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-2 text-center">
                      {messages.filter(m => m.role === 'user').length} {t('chat.messagesInHistory')}
                    </p>
                  )}
                </div>
                
                <div className="text-xs text-muted-foreground">
                  <p>
                    {mode === 'full_docs' 
                      ? t('chat.analyzingFullDocs') 
                      : t('chat.usingSummaries')}
                  </p>
                </div>
              </div>
            )}
          </div>
          
          {/* Messages */}
          <ScrollArea className="flex-1 p-4 overflow-x-hidden">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <Bot className="h-16 w-16 mb-4 text-muted-foreground opacity-30" />
                <p className="text-sm text-muted-foreground font-medium">
                  {t('chat.askQuestions')}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  {t('chat.poweredBy')}
                </p>
                {hasContext && (
                  <div className="mt-4 space-y-1 text-xs text-muted-foreground">
                    <p>{t('chat.tryAsking')}</p>
                    <p className="italic">"What are the key requirements?"</p>
                    <p className="italic">"Summarize the main points"</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4 w-full max-w-full overflow-hidden">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={cn(
                      'flex gap-3 w-full',
                      message.role === 'user' ? 'justify-end' : 'justify-start'
                    )}
                  >
                    <div
                      className={cn(
                        'rounded-lg p-3 max-w-full',
                        message.role === 'user'
                          ? 'bg-primary'
                          : message.role === 'sources'
                          ? 'bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800'
                          : message.role === 'performance'
                          ? 'bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800'
                          : 'bg-muted'
                      )}
                    >
                      {message.role === 'user' ? (
                        <div className="text-white">
                          <p className="text-sm">{message.content}</p>
                          <p className="text-xs opacity-80 mt-2">
                            {message.timestamp.toLocaleTimeString()}
                          </p>
                        </div>
                      ) : message.role === 'sources' && message.sources ? (
                        <div>
                          <p className="text-sm font-semibold mb-2 text-blue-900 dark:text-blue-100">
                            🔍 {message.content}
                          </p>
                          <div className="space-y-1">
                            {message.sources.map((source, idx) => {
                              const relevancePercent = source.relevance_score ? Math.round(source.relevance_score * 100) : 0;
                              const showRelevance = relevancePercent > 1;
                              
                              return (
                                <div key={idx} className="text-xs text-gray-700 dark:text-gray-300">
                                  📄 [{idx + 1}] {source.filename}
                                  {showRelevance && ` (${relevancePercent}% relevant)`}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : message.role === 'performance' && message.performance ? (
                        <div>
                          <p className="text-xs font-semibold mb-2">{t('chat.performance')}</p>
                          <div className="space-y-1 text-[10px] text-muted-foreground">
                            <div className="flex justify-between">
                              <span>{t('chat.totalTime')}:</span>
                              <span className="font-mono">
                                {message.performance.total_time_seconds.toFixed(2)}s
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>{t('chat.documentsUsed')}:</span>
                              <span className="font-mono">{message.performance.documents_used}</span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm prose prose-sm dark:prose-invert max-w-full overflow-hidden">
                          <ReactMarkdown
                            components={{
                              h2: ({node, ...props}) => <h2 className="text-lg font-bold mt-4 mb-2 text-gray-900 dark:text-gray-100" {...props} />,
                              h3: ({node, ...props}) => <h3 className="text-base font-semibold mt-3 mb-1 text-gray-800 dark:text-gray-200" {...props} />,
                              ul: ({node, ...props}) => <ul className="list-disc pl-5 my-2 space-y-1" {...props} />,
                              ol: ({node, ...props}) => <ol className="list-decimal pl-5 my-2 space-y-1" {...props} />,
                              li: ({node, ...props}) => <li className="ml-0 text-gray-700 dark:text-gray-300" {...props} />,
                              strong: ({node, ...props}) => <strong className="font-semibold text-gray-900 dark:text-gray-100" {...props} />,
                              p: ({node, ...props}) => <p className="my-2 text-gray-700 dark:text-gray-300" {...props} />,
                              table: ({node, ...props}) => <table className="border-collapse border border-gray-300 my-3 w-full" {...props} />,
                              th: ({node, ...props}) => <th className="border border-gray-300 px-3 py-2 bg-gray-100 dark:bg-gray-800 text-left" {...props} />,
                              td: ({node, ...props}) => <td className="border border-gray-300 px-3 py-2" {...props} />,
                            }}
                          >
                            {message.content}
                          </ReactMarkdown>
                        </div>
                      )}
                      
                      {message.role !== 'user' && (
                        <p className="text-xs opacity-60 mt-2">
                          {message.timestamp.toLocaleTimeString()}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
                
                {loading && (
                  <div className="flex gap-3">
                    <Bot className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <div className="bg-muted rounded-lg p-3">
                      <p className="text-sm text-muted-foreground mb-1">{t('chat.thinking')}</p>
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-current rounded-full animate-bounce" />
                        <div className="w-2 h-2 bg-current rounded-full animate-bounce delay-75" />
                        <div className="w-2 h-2 bg-current rounded-full animate-bounce delay-150" />
                      </div>
                    </div>
                  </div>
                )}
                {/* Invisible anchor for auto-scroll */}
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>
          
          {/* Input */}
          <div className="p-4 border-t bg-background">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={hasContext ? t('chat.placeholder') : t('chat.enterProjectFirst')}
                disabled={loading || !hasContext}
                className="flex-1"
              />
              <Button
                onClick={handleSend}
                disabled={loading || !input.trim() || !hasContext}
                size="icon"
              >
                <Send className="h-4 w-4" aria-label={t('chat.send')} />
              </Button>
            </div>
            {hasContext && (
              <p className="text-xs text-muted-foreground mt-2">
                {useStreaming ? t('chat.streaming') : t('chat.standard')} • {mode === 'full_docs' ? t('chat.fullDocs') : t('chat.summaries')} • {model}
              </p>
            )}
          </div>
        </>
      </aside>
    </div>
  );
}
